import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AppName, SignedOutReason } from '@despachador/api-client';
import { AUTH_ERRORS } from '@despachador/contracts/auth-errors';
import { Notice } from '../index';
import { AuthCard, type AuthBrand } from './pieces';

/** Status screens without a session: `/acceso/estado/:motivo`. */
export type StatusMotive =
  | 'sesion-expirada'
  | 'sesion-cerrada'
  | 'cuenta-suspendida'
  | 'cuenta-desactivada'
  | 'despacho-suspendido'
  | 'app-no-permitida'
  | 'servicio-no-disponible';

export const STATUS_MOTIVES: readonly StatusMotive[] = [
  'sesion-expirada',
  'sesion-cerrada',
  'cuenta-suspendida',
  'cuenta-desactivada',
  'despacho-suspendido',
  'app-no-permitida',
  'servicio-no-disponible',
];

export const isStatusMotive = (v: string): v is StatusMotive => (STATUS_MOTIVES as readonly string[]).includes(v);

/** The screen for a session that ended; null = straight to the sign-in screen. */
export const MOTIVE_OF_REASON: Record<SignedOutReason, StatusMotive | null> = {
  none: null,
  expired: 'sesion-expirada',
  revoked: 'sesion-cerrada',
  suspended: 'cuenta-suspendida',
  disabled: 'cuenta-desactivada',
  tenant_suspended: 'despacho-suspendido',
  unavailable: 'servicio-no-disponible',
};

/** The screen for an answer of the sign-in that is only told after a valid credential. */
export const MOTIVE_OF_CODE: Record<string, StatusMotive | undefined> = {
  ACCOUNT_SUSPENDED: 'cuenta-suspendida',
  ACCOUNT_DISABLED: 'cuenta-desactivada',
  TENANT_SUSPENDED: 'despacho-suspendido',
  APP_NOT_ALLOWED: 'app-no-permitida',
};

interface MotiveText {
  title: string;
  text: string;
  tone: 'info' | 'warn' | 'danger';
  /** Main action: sign in again, or try again (service unavailable). */
  action: 'signin' | 'retry';
  actionLabel: string;
}

const OTHER_APP: Record<AppName, string> = {
  admin: 'Si eres conductor, ingresa desde la app del conductor.',
  driver: 'El personal de la central ingresa desde el panel de operación.',
};

function motiveText(motive: StatusMotive, app: AppName): MotiveText {
  switch (motive) {
    case 'sesion-expirada':
      return { title: 'Tu sesión terminó', text: `${AUTH_ERRORS.SESSION_EXPIRED.user} Por seguridad, las sesiones se cierran tras un tiempo sin uso.`, tone: 'info', action: 'signin', actionLabel: 'Ingresar de nuevo' };
    case 'sesion-cerrada':
      return {
        title: 'Tu sesión se cerró',
        text: `${AUTH_ERRORS.SESSION_REVOKED.user} Se cerró desde otro dispositivo, por un cambio de seguridad o por el administrador de tu empresa. Si no fuiste tú, avísale.`,
        tone: 'warn',
        action: 'signin',
        actionLabel: 'Ingresar de nuevo',
      };
    case 'cuenta-suspendida':
      return { title: 'Cuenta suspendida', text: AUTH_ERRORS.ACCOUNT_SUSPENDED.user, tone: 'danger', action: 'signin', actionLabel: 'Volver al ingreso' };
    case 'cuenta-desactivada':
      return { title: 'Cuenta desactivada', text: AUTH_ERRORS.ACCOUNT_DISABLED.user, tone: 'danger', action: 'signin', actionLabel: 'Volver al ingreso' };
    case 'despacho-suspendido':
      return { title: 'Acceso de la empresa suspendido', text: AUTH_ERRORS.TENANT_SUSPENDED.user, tone: 'danger', action: 'signin', actionLabel: 'Volver al ingreso' };
    case 'app-no-permitida':
      return { title: 'Esta aplicación no es para tu cuenta', text: `${AUTH_ERRORS.APP_NOT_ALLOWED.user} ${OTHER_APP[app]}`, tone: 'warn', action: 'signin', actionLabel: 'Ingresar con otra cuenta' };
    case 'servicio-no-disponible':
      return {
        title: 'Sin conexión con el servidor',
        text: 'No pudimos comprobar tu sesión porque el servidor no responde o no hay internet. Tu sesión no se cerró: inténtalo de nuevo en un momento.',
        tone: 'warn',
        action: 'retry',
        actionLabel: 'Reintentar',
      };
  }
}

/**
 * A screen without a session that explains a situation and offers one clear action (plus an
 * optional secondary one). Shared by the status screens and the problems of an activation.
 */
export function StatusCard({
  brand,
  title,
  tone,
  children,
  action,
  secondary,
  large,
  busy,
}: {
  brand: AuthBrand;
  title: string;
  tone: 'info' | 'warn' | 'danger';
  children: ReactNode;
  action: { label: string; busyLabel?: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  large?: boolean;
  busy?: boolean;
}) {
  return (
    <AuthCard brand={brand} title={title}>
      <Notice tone={tone}>{children}</Notice>
      <button type="button" className={`btn primary block ${large ? 'lg' : ''}`} disabled={busy} onClick={action.onClick}>
        {busy ? (action.busyLabel ?? action.label) : action.label}
      </button>
      {secondary && (
        <button type="button" className="btn ghost sm" onClick={secondary.onClick}>
          {secondary.label}
        </button>
      )}
    </AuthCard>
  );
}

/**
 * Why there is no session and what to do about it: one clear action (sign in again, or retry).
 * Never says more than the API said: account states only come after a valid credential.
 */
export function StatusScreen({ motive, app, brand, large, busy, onSignIn, onRetry }: { motive: StatusMotive; app: AppName; brand: AuthBrand; large?: boolean; busy?: boolean; onSignIn: () => void; onRetry: () => void }) {
  const t = motiveText(motive, app);
  return (
    <StatusCard
      brand={brand}
      title={t.title}
      tone={t.tone}
      large={large}
      busy={busy}
      action={{ label: t.actionLabel, busyLabel: 'Comprobando…', onClick: t.action === 'retry' ? onRetry : onSignIn }}
      secondary={t.action === 'retry' ? { label: 'Ir al ingreso', onClick: onSignIn } : undefined}
    >
      {t.text}
    </StatusCard>
  );
}

/** A section the signed-in person has no permission for: says so instead of redirecting in silence. */
export function NoPermission({ action }: { action?: ReactNode }) {
  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <h1 className="h3">
        <ShieldAlert size={20} aria-hidden /> No tienes permiso para ver esta sección
      </h1>
      <Notice tone="warn">Tu usuario no tiene el permiso que pide esta pantalla. Si lo necesitas para tu trabajo, pídelo al administrador de tu empresa.</Notice>
      {action && <div>{action}</div>}
    </div>
  );
}
